import React from "react";
import {
  InputGroup,
  Tag,
  Button,
  Card,
  Icon,
  Intent,
  Popover,
  Position,
  Menu,
  MenuItem,
  ButtonGroup,
  ControlGroup,
  Divider,
} from "@blueprintjs/core";
import { TagInput } from "@blueprintjs/core";
import _ from "lodash";
import "./NewMultiSelector.css";

/**
 * Reusable NewMultiSelector component - A lego/plug-and-play component
 * Features:
 * 1. Type to search/filter items
 * 2. Multiple selection
 * 3. Select all functionality
 * 4. Individual item removal
 * 5. Sorting support
 * 6. Dropdown that doesn't cover the search field
 */
class NewMultiSelector extends React.Component {
  state = {
    searchText: "",
    isOpen: false,
    filteredItems: [],
    highlightedIndex: 0,
  };

  inputRef = null;

  componentDidMount() {
    this.updateFilteredItems();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.items) {
      this.updateFilteredItems();
    }
  }

  updateFilteredItems = () => {
    const { items = [], sortItems = true } = this.props;
    const { searchText } = this.state;

    let filtered = items.filter((item) => {
      const itemText = this.getItemText(item);
      return itemText.toLowerCase().includes(searchText.toLowerCase());
    });

    if (sortItems) {
      filtered = filtered.sort((a, b) => {
        const textA = this.getItemText(a);
        const textB = this.getItemText(b);
        return textA.localeCompare(textB);
      });
    }

    this.setState({ filteredItems: filtered, highlightedIndex: 0 });
  };

  getItemText = (item) => {
    const { itemTextProp } = this.props;
    if (typeof item === "string") return item;
    if (itemTextProp && item[itemTextProp]) return item[itemTextProp];
    return item.name || item.label || item.text || String(item);
  };

  getItemValue = (item) => {
    const { itemValueProp } = this.props;
    if (typeof item === "string") return item;
    if (itemValueProp && item[itemValueProp]) return item[itemValueProp];
    return item.value || item.id || this.getItemText(item);
  };

  handleSearchChange = (valueOrEvent) => {
    // Support TagInput onInputChange (string) and InputGroup onChange (event)
    const searchText = typeof valueOrEvent === "string" ? valueOrEvent : valueOrEvent.target.value;
    this.setState(
      {
        searchText,
        isOpen: true, // Ensure dropdown opens when typing
      },
      () => {
        this.updateFilteredItems();
        // Keep focus in the input so user can continue typing
        if (this.inputRef && typeof this.inputRef.focus === "function") {
          this.inputRef.focus();
        }
      }
    );
  };

  handleKeyDown = (e) => {
    const { filteredItems, highlightedIndex } = this.state;
    if (!filteredItems || filteredItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.setState({ highlightedIndex: (highlightedIndex + 1) % filteredItems.length, isOpen: true });
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      this.setState({
        highlightedIndex: (highlightedIndex - 1 + filteredItems.length) % filteredItems.length,
        isOpen: true,
      });
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const item = filteredItems[highlightedIndex];
      if (item) this.handleItemToggle(item);
    }
    if (e.key === "Escape") {
      e.preventDefault();
      this.setState({ isOpen: false });
    }
  };

  handleItemToggle = (item) => {
    const { selectedItems = [], onChange } = this.props;
    const itemValue = this.getItemValue(item);

    const isSelected = selectedItems.some((selected) => this.getItemValue(selected) === itemValue);

    let newSelection;
    if (isSelected) {
      // Remove the item
      newSelection = selectedItems.filter((selected) => this.getItemValue(selected) !== itemValue);
    } else {
      // Add the item
      newSelection = [...selectedItems, item];
    }

    if (onChange) {
      onChange(newSelection);
    }

    // Keep dropdown open after selection to allow multiple selections
    // Don't close the dropdown here
  };

  handleSelectAll = () => {
    const { onChange } = this.props;
    const { filteredItems } = this.state;

    if (onChange) {
      onChange(filteredItems);
    }
  };

  handleClearAll = () => {
    const { onChange } = this.props;

    if (onChange) {
      onChange([]);
    }
  };

  handleRemoveItem = (itemToRemove) => {
    const { selectedItems = [], onChange } = this.props;
    const itemValue = this.getItemValue(itemToRemove);

    const newSelection = selectedItems.filter((item) => this.getItemValue(item) !== itemValue);

    if (onChange) {
      onChange(newSelection);
    }
  };

  renderSelectedItems = () => {
    const { selectedItems = [], maxDisplayItems = 3 } = this.props;

    if (selectedItems.length === 0) {
      return (
        <Tag minimal intent="warning" style={{ margin: "2px" }}>
          No items selected
        </Tag>
      );
    }

    const displayItems = selectedItems.slice(0, maxDisplayItems);
    const remainingCount = selectedItems.length - maxDisplayItems;

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
        {displayItems.map((item, index) => (
          <Tag
            key={index}
            intent="primary"
            interactive
            style={{
              borderRadius: "8px",
              fontSize: "11px",
              padding: "2px 6px",
              background: "#137cbd",
              color: "white",
            }}
            onRemove={() => this.handleRemoveItem(item)}
          >
            {this.getItemText(item)}
          </Tag>
        ))}
        {remainingCount > 0 && (
          <Tag
            intent="success"
            style={{
              borderRadius: "8px",
              fontSize: "11px",
              padding: "2px 6px",
              background: "#0f9960",
              color: "white",
            }}
          >
            +{remainingCount} more
          </Tag>
        )}
      </div>
    );
  };

  renderDropdownContent = () => {
    const { selectedItems = [], loading = false, emptyMessage = "No items available" } = this.props;
    const { filteredItems, searchText, highlightedIndex } = this.state;

    if (loading) {
      return (
        <Menu>
          <MenuItem disabled text="Loading..." icon="refresh" />
        </Menu>
      );
    }

    if (filteredItems.length === 0) {
      return (
        <Menu>
          <MenuItem disabled text={searchText ? `No items matching "${searchText}"` : emptyMessage} icon="search" />
        </Menu>
      );
    }

    const allSelected = filteredItems.every((item) =>
      selectedItems.some((selected) => this.getItemValue(selected) === this.getItemValue(item))
    );

    return (
      <div style={{ maxWidth: "400px" }}>
        {/* Header with select all/clear all */}
        <div
          style={{
            padding: "8px 12px",
            borderBottom: "1px solid #394b59",
            background: "#30404d",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#a7b6c2",
              }}
            >
              {filteredItems.length} items • {selectedItems.length} selected
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <ButtonGroup minimal>
                <Button
                  small
                  intent={Intent.PRIMARY}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.handleSelectAll();
                  }}
                  disabled={allSelected || filteredItems.length === 0}
                  style={{ fontSize: "11px" }}
                >
                  Select All
                </Button>
                <Button
                  small
                  intent={Intent.WARNING}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.handleClearAll();
                  }}
                  disabled={selectedItems.length === 0}
                  style={{ fontSize: "11px" }}
                >
                  Clear All
                </Button>
              </ButtonGroup>
              <Button
                small
                minimal
                icon="cross"
                onClick={(e) => {
                  e.stopPropagation();
                  this.setState({ isOpen: false });
                }}
                style={{ fontSize: "11px", marginLeft: "8px" }}
                title="Close dropdown"
              />
            </div>
          </div>
        </div>

        {/* Items list */}
        <Menu
          style={{
            maxHeight: "250px",
            overflowY: "auto",
            padding: "4px",
          }}
        >
          {filteredItems.map((item, index) => {
            const isSelected = selectedItems.some(
              (selected) => this.getItemValue(selected) === this.getItemValue(item)
            );

            return (
              <MenuItem
                key={index}
                text={this.getItemText(item)}
                active={index === highlightedIndex}
                icon={isSelected ? "tick" : "blank"}
                shouldDismissPopover={false}
                onClick={() => this.handleItemToggle(item)}
                className={isSelected ? "selected-item" : ""}
                style={{
                  background: isSelected ? "rgba(19, 124, 189, 0.3)" : "transparent",
                  borderRadius: "4px",
                  margin: "2px 0",
                }}
              />
            );
          })}
        </Menu>
      </div>
    );
  };

  render() {
    const {
      placeholder = "Search and select items...",
      disabled = false,
      selectedItems = [],
      label,
      showLabel = true,
      intent = Intent.NONE,
      style = {},
    } = this.props;

    const { searchText, isOpen } = this.state;

    return (
      <div style={{ position: "relative", ...style }}>
        {showLabel && label && (
          <Tag large minimal style={{ marginBottom: "4px" }}>
            {label}
          </Tag>
        )}

        <ControlGroup fill>
          <Popover
            content={this.renderDropdownContent()}
            isOpen={isOpen}
            onInteraction={(nextOpenState, event) => {
              if (!disabled) {
                // Don't close if clicking inside the popover content
                if (event && event.target && event.target.closest(".multi-selector-popover")) {
                  return;
                }
                this.setState({ isOpen: nextOpenState });
              }
            }}
            position={Position.BOTTOM_LEFT}
            popoverClassName="multi-selector-popover"
            minimal
            boundary="viewport"
            autoFocus={false}
            enforceFocus={false}
            modifiers={{
              preventOverflow: { enabled: true, boundariesElement: "viewport" },
              flip: { enabled: true },
              offset: { enabled: true, offset: "0, 5" },
            }}
            interactionKind="click"
            captureDismiss={false}
          >
            <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
              <TagInput
                values={[...selectedItems.map((i) => this.getItemText(i)).sort((a, b) => a.localeCompare(b))]}
                inputValue={searchText}
                onInputChange={this.handleSearchChange}
                onRemove={(value, index) => {
                  const toRemoveText = value;
                  const match = selectedItems.find((si) => this.getItemText(si) === toRemoveText);
                  if (match) this.handleRemoveItem(match);
                }}
                placeholder={placeholder}
                disabled={disabled}
                onKeyDown={this.handleKeyDown}
                inputRef={(ref) => {
                  this.inputRef = ref;
                }}
                rightElement={
                  <Button
                    icon={isOpen ? "chevron-up" : "chevron-down"}
                    minimal
                    disabled={disabled}
                    onClick={() => !disabled && this.setState({ isOpen: !isOpen })}
                  />
                }
                addOnBlur={false}
                tagProps={{ minimal: true }}
                fill
              />
            </div>
          </Popover>
        </ControlGroup>

        {/* Selected items display */}
        {selectedItems.length > 0 && (
          <Card
            style={{
              marginTop: "8px",
              padding: "12px",
              background: "#394b59",
              border: "1px solid #5c7080",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#a7b6c2",
                }}
              >
                Selected Items ({selectedItems.length})
              </span>
              {selectedItems.length > 0 && (
                <Button
                  small
                  minimal
                  intent={Intent.DANGER}
                  icon="cross"
                  onClick={this.handleClearAll}
                  style={{ fontSize: "10px" }}
                >
                  Clear All
                </Button>
              )}
            </div>
            {this.renderSelectedItems()}
          </Card>
        )}
      </div>
    );
  }
}

export default NewMultiSelector;
